import { ref, computed } from 'vue'
import { backgroundMusic } from '@/data/music.js'
import { useI18n } from '@/i18n'

export function useMusic() {
  const { t } = useI18n()
  
  const audioElement = ref(null)
  const isPlaying = ref(false)
  const isMuted = ref(false)
  const currentTrackIndex = ref(Math.floor(Math.random() * backgroundMusic.length))
  const volume = ref(30)
  const duration = ref(0)
  const currentTime = ref(0)
  const isLoading = ref(false)
  const uploadedTracks = ref([])
  
  // 转换音乐数据为本地化版本
  const getLocalizedTrack = (track) => {
    if (track.titleKey) {
      // 预设音乐使用国际化
      return {
        title: t(`music.${track.titleKey}`),
        artist: t(`music.${track.artistKey}`),
        url: track.url,
        duration: t(`music.${track.durationKey}`)
      }
    } else {
      // 用户上传的音乐保持原样
      return track
    }
  }
  
  const localizedBackgroundMusic = computed(() => 
    backgroundMusic.map(track => getLocalizedTrack(track))
  )
  
  const allTracks = computed(() => [...localizedBackgroundMusic.value, ...uploadedTracks.value])
  const currentTrack = computed(() => allTracks.value[currentTrackIndex.value])
  
  const initAudioPlayer = () => {
    try {
      audioElement.value = new Audio()
      audioElement.value.loop = true
      audioElement.value.volume = volume.value / 100
      audioElement.value.preload = 'metadata'
      
      // 添加事件监听器
      audioElement.value.addEventListener('loadstart', () => {
        isLoading.value = true
      })
      
      audioElement.value.addEventListener('loadedmetadata', () => {
        duration.value = audioElement.value.duration
        isLoading.value = false
      })
      
      audioElement.value.addEventListener('timeupdate', () => {
        currentTime.value = audioElement.value.currentTime
      })
      
      audioElement.value.addEventListener('ended', () => {
        nextTrack()
      })
      
      audioElement.value.addEventListener('error', (e) => {
        console.warn('音频加载失败:', e)
        isLoading.value = false
      })
      
      // 加载第一首歌
      loadTrack()
    } catch (e) {
      console.warn('Audio API不可用')
    }
  }
  
  const loadTrack = () => {
    if (!audioElement.value) return
    
    const track = currentTrack.value
    audioElement.value.src = track.url
    audioElement.value.load()
  }
  
  const playMusic = async () => {
    if (!audioElement.value) return
    
    try {
      await audioElement.value.play()
      isPlaying.value = true
    } catch (e) {
      console.warn('音频播放失败:', e)
    }
  }
  
  const stopMusic = () => {
    if (!audioElement.value) return
    
    audioElement.value.pause()
    isPlaying.value = false
  }
  
  const togglePlay = () => {
    if (isPlaying.value) {
      stopMusic()
    } else {
      playMusic()
    }
  }
  
  const nextTrack = () => {
    const wasPlaying = isPlaying.value
    
    if (isPlaying.value) {
      stopMusic()
    }
    
    currentTrackIndex.value = (currentTrackIndex.value + 1) % allTracks.value.length
    loadTrack()
    
    if (wasPlaying) {
      setTimeout(() => playMusic(), 100)
    }
  }
  
  const toggleMute = () => {
    if (!audioElement.value) return
    
    isMuted.value = !isMuted.value
    audioElement.value.muted = isMuted.value
  }
  
  const setVolume = (value) => {
    if (!audioElement.value) return
    
    volume.value = value
    const vol = value / 100
    audioElement.value.volume = vol
    
    isMuted.value = vol === 0
    if (vol > 0 && audioElement.value.muted) {
      audioElement.value.muted = false
      isMuted.value = false
    }
  }
  
  const addUploadedTrack = (file) => {
    return new Promise((resolve, reject) => {
      if (!file || !file.type.startsWith('audio/')) {
        reject(new Error(t('settings.selectAudioFile') || '请选择音频文件'))
        return
      }
      
      const url = URL.createObjectURL(file)
      const track = {
        title: file.name.replace(/\.[^/.]+$/, ""),
        artist: t('settings.userUploaded') || '用户上传',
        url: url,
        duration: t('music.unknown'),
        isUploaded: true
      }
      
      uploadedTracks.value.push(track)
      resolve(track)
    })
  }
  
  const removeUploadedTrack = (index) => {
    const track = uploadedTracks.value[index]
    if (track && track.isUploaded) {
      URL.revokeObjectURL(track.url)
      uploadedTracks.value.splice(index, 1)
      
      // 如果删除的是当前播放的曲目，切换到下一首
      const trackIndexInAll = backgroundMusic.length + index
      if (currentTrackIndex.value === trackIndexInAll) {
        if (allTracks.value.length > 0) {
          currentTrackIndex.value = currentTrackIndex.value % allTracks.value.length
          if (isPlaying.value) {
            loadTrack()
            setTimeout(() => playMusic(), 100)
          }
        }
      } else if (currentTrackIndex.value > trackIndexInAll) {
        currentTrackIndex.value--
      }
    }
  }
  
  const playTrack = (index) => {
    const wasPlaying = isPlaying.value
    
    if (isPlaying.value) {
      stopMusic()
    }
    
    currentTrackIndex.value = index
    loadTrack()
    
    if (wasPlaying || !isPlaying.value) {
      setTimeout(() => playMusic(), 100)
    }
  }

  return {
    currentTrack,
    isPlaying,
    isMuted,
    volume,
    duration,
    currentTime,
    isLoading,
    uploadedTracks,
    allTracks,
    currentTrackIndex,
    initAudioPlayer,
    playMusic,
    stopMusic,
    togglePlay,
    nextTrack,
    toggleMute,
    setVolume,
    loadTrack,
    addUploadedTrack,
    removeUploadedTrack,
    playTrack
  }
}